import PropTypes from 'prop-types';
import { useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import Iconify from '../Iconify';
import MenuPopover from '../MenuPopover';
import { useDispatch } from '../../redux/store';
import { deleteConversation, renameConversation } from '../../redux/slices/aiConversation';
import { setDialogOpen } from '../../redux/slices/confirmDialog';
import useLocales from '../../hooks/useLocales';

// ----------------------------------------------------------------------

ChatNavItem.propTypes = {
  selected: PropTypes.bool,
  collapse: PropTypes.bool,
  conversation: PropTypes.object,
  onSelectConversation: PropTypes.func
};

export function ChatNavItem({ selected, collapse, conversation, onSelectConversation }) {
  const dispatch = useDispatch();
  const { translate: t } = useLocales();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [openRename, setOpenRename] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  let totalMessages = 0;
  if (conversation?.messages) {
    if (Array.isArray(conversation.messages)) {
      totalMessages = conversation.messages.length;
    } else {
      totalMessages = Object.values(conversation.messages).flat().length;
    }
  }

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor(null);
  };

  const handleOpenRename = (event) => {
    event.stopPropagation();
    handleCloseMenu();
    setNewTitle(conversation?.title || '');
    setOpenRename(true);
  };

  const handleSaveRename = async () => {
    if (!newTitle.trim() || !conversation?.id) return;
    try {
      await dispatch(renameConversation({ id: conversation.id, title: newTitle.trim() }));
      setOpenRename(false);
    } catch (err) {
      // Handled in Redux slice
    }
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    handleCloseMenu();
    dispatch(
      setDialogOpen({
        title: t('shared.confirmDeleteTitle', 'Confirmer la suppression'),
        message: t('chat.deleteConfirm', 'Êtes-vous sûr de vouloir supprimer cette conversation ?'),
        okLabel: t('shared.formYes', 'Oui'),
        cancelLabel: t('shared.formNo', 'Non'),
        onOK: () => {
          dispatch(deleteConversation(conversation.id));
        }
      })
    );
  };

  return (
    <>
      <Box component="li" sx={{ display: 'flex' }}>
        <ListItemButton
          onClick={onSelectConversation}
          sx={{
            py: 1.5,
            px: 2,
            gap: 1.5,
            position: 'relative',
            ...(selected && { bgcolor: 'action.selected' }),
            '&:hover .action-button': {
              opacity: 1
            }
          }}
        >
          {!collapse && (
            <>
              <ListItemText
                primary={conversation?.title}
                primaryTypographyProps={{
                  noWrap: true,
                  component: 'span',
                  variant: 'subtitle2',
                  sx: { maxWidth: 105 }
                }}
                secondary={conversation?.created_at}
                secondaryTypographyProps={{
                  noWrap: true,
                  component: 'span',
                  variant: 'caption',
                  color: 'text.secondary'
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ml: 'auto'
                }}
              >
                {totalMessages > 0 && (
                  <Typography noWrap variant="caption" component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>
                    {totalMessages}
                  </Typography>
                )}

                {conversation?.id && (
                  <IconButton
                    className="action-button"
                    size="small"
                    onClick={handleOpenMenu}
                    sx={{
                      p: 0.5,
                      opacity: selected ? 0.8 : 0,
                      transition: (theme) => theme.transitions.create('opacity'),
                      '&:hover': { opacity: 1 }
                    }}
                  >
                    <Iconify icon="eva:more-vertical-fill" width={16} height={16} />
                  </IconButton>
                )}
              </Box>
            </>
          )}
        </ListItemButton>
      </Box>

      {conversation?.id && (
        <MenuPopover
          open={Boolean(menuAnchor)}
          anchorEl={menuAnchor}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          arrow="right-top"
          sx={{
            mt: -0.5,
            width: 160,
            '& .MuiMenuItem-root': {
              px: 1.5,
              py: 0.75,
              typography: 'body2',
              borderRadius: 0.75,
              '& svg': { mr: 1.5, width: 18, height: 18 }
            }
          }}
        >
          <MenuItem onClick={handleOpenRename}>
            <Iconify icon="eva:edit-2-outline" />
            {t('chat.rename', 'Renommer')}
          </MenuItem>
          <Divider sx={{ borderStyle: 'dashed', my: 0.5 }} />
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <Iconify icon="eva:trash-2-outline" />
            {t('chat.delete', 'Supprimer')}
          </MenuItem>
        </MenuPopover>
      )}

      {conversation?.id && (
        <Dialog
          open={openRename}
          onClose={() => setOpenRename(false)}
          onClick={(e) => e.stopPropagation()}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>{t('chat.renameTitle', 'Renommer la conversation')}</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              variant="outlined"
              label={t('chat.title', 'Titre')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTitle.trim()) {
                  handleSaveRename();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRename(false)} color="inherit">
              {t('shared.formCancel', 'Annuler')}
            </Button>
            <Button onClick={handleSaveRename} variant="contained" disabled={!newTitle.trim()}>
              {t('shared.formSave', 'Enregistrer')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
